import React from 'react';
import { render, cleanup, act } from '@testing-library/react';
import { withWrapper } from '../testWrapper';
import { enableMocking, disableMocking, MOCK_DB } from '../mockApi';
import { captureComponentSnapshot, createLogCapture } from '../snapshotCapture';

function assert(condition, message) {
  if (!condition) throw new Error(message);
}

async function renderWithMock(element, assertions, waitMs = 400) {
  enableMocking();
  let snapshot = null;
  const logCapture = createLogCapture();
  try {
    let result;
    // Phase 1: render and flush initial effects so useEffect API calls are made
    await act(async () => {
      result = render(element);
    });
    // Phase 2: wait for mock responses; setState calls during this window land in
    // the act queue and are committed when the callback returns
    await act(async () => {
      await new Promise(r => setTimeout(r, waitMs));
    });
    await assertions(result);
    snapshot = captureComponentSnapshot(result.container);
  } finally {
    cleanup();
    disableMocking();
    logCapture.restore();
  }
  return { snapshot, logs: logCapture.getLogs() };
}

// ─── Expected values from MOCK_DB ────────────────────────────────────────────

const EXPECTED_PROJECTS = MOCK_DB.projects.length; // 3
const EXPECTED_OPEN_TASKS = MOCK_DB.tasks.filter(
  t => t.status?.name?.toLowerCase() !== 'done'
).length; // tasks not done
const EXPECTED_UNREAD = MOCK_DB.conversations.reduce(
  (sum, c) => sum + (c.unread_count || 0), 0
); // sum of unread_count

export const dashboardTests = [

  // ─── Statistics block ───────────────────────────────────────────────────────

  {
    id: 'dash-render-1',
    name: 'Dashboard renders without crashing',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          assert(container.firstChild !== null, 'Dashboard should render');
        }
      );
    },
  },

  {
    id: 'dash-stat-projects',
    name: 'Statistics: projects count matches all user projects',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const el = container.querySelector('[data-testid="dashboard-stat-projects"]');
          assert(el !== null, 'dashboard-stat-projects element must exist');
          assert(
            el.textContent === String(EXPECTED_PROJECTS),
            `Expected ${EXPECTED_PROJECTS} projects, got "${el.textContent}"`
          );
        }
      );
    },
  },

  {
    id: 'dash-stat-tasks',
    name: 'Statistics: open tasks count excludes "done" tasks from all projects',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const el = container.querySelector('[data-testid="dashboard-stat-tasks"]');
          assert(el !== null, 'dashboard-stat-tasks element must exist');
          assert(
            el.textContent === String(EXPECTED_OPEN_TASKS),
            `Expected ${EXPECTED_OPEN_TASKS} open tasks, got "${el.textContent}"`
          );
        }
      );
    },
  },

  {
    id: 'dash-stat-conversations',
    name: 'Statistics: unread conversations count is sum across all projects',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const el = container.querySelector('[data-testid="dashboard-stat-conversations"]');
          assert(el !== null, 'dashboard-stat-conversations element must exist');
          assert(
            el.textContent === String(EXPECTED_UNREAD),
            `Expected ${EXPECTED_UNREAD} unread, got "${el.textContent}"`
          );
        }
      );
    },
  },

  // ─── Calendar block ─────────────────────────────────────────────────────────

  {
    id: 'dash-calendar-render',
    name: 'Calendar: renders with prev/next navigation buttons',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const prev = container.querySelector('[data-testid="dashboard-calendar-prev"]');
          const next = container.querySelector('[data-testid="dashboard-calendar-next"]');
          const heading = container.querySelector('[data-testid="dashboard-calendar-heading"]');
          assert(prev !== null, 'Prev month button must exist');
          assert(next !== null, 'Next month button must exist');
          assert(heading !== null, 'Calendar heading must exist');
          assert(heading.textContent.trim().length > 0, 'Calendar heading must not be empty');
        }
      );
    },
  },

  {
    id: 'dash-calendar-meeting-dots',
    name: 'Calendar: meeting dots appear on days that have meetings from any project',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          // MOCK_DB meetings are on days 5, 12, 20 of this month
          const expectedDays = [5, 12, 20];
          expectedDays.forEach(day => {
            const dot = container.querySelector(`[data-testid="dashboard-calendar-dot-${day}"]`);
            assert(dot !== null, `Meeting dot for day ${day} must be rendered`);
          });
        }
      );
    },
  },

  {
    id: 'dash-calendar-no-dot-days',
    name: 'Calendar: days without meetings have no dot',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          // Days 1, 2, 3 have no meetings in mock data
          [1, 2, 3].forEach(day => {
            const dot = container.querySelector(`[data-testid="dashboard-calendar-dot-${day}"]`);
            assert(dot === null, `Day ${day} should NOT have a meeting dot`);
          });
        }
      );
    },
  },

  // ─── All Projects widget ────────────────────────────────────────────────────

  {
    id: 'dash-projects-widget',
    name: 'All Projects widget: lists every project from the API response',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          MOCK_DB.projects.forEach(p => {
            const btn = container.querySelector(`[data-testid="dashboard-project-${p.uuid}"]`);
            assert(btn !== null, `Project button for "${p.name}" (${p.uuid}) must exist`);
            assert(
              btn.textContent.includes(p.name),
              `Project card must show name "${p.name}"`
            );
          });
        }
      );
    },
  },

  {
    id: 'dash-projects-widget-count',
    name: 'All Projects widget: shows same number of projects as API returns',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const buttons = container.querySelectorAll('[data-testid^="dashboard-project-"]');
          assert(
            buttons.length === MOCK_DB.projects.length,
            `Expected ${MOCK_DB.projects.length} project buttons, found ${buttons.length}`
          );
        }
      );
    },
  },

  {
    id: 'dash-manage-projects-btn',
    name: 'All Projects widget: Manage Projects button exists',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const btn = container.querySelector('[data-testid="dashboard-manage-projects-btn"]');
          assert(btn !== null, 'Manage Projects button must exist');
        }
      );
    },
  },

  // ─── Conversations widget ───────────────────────────────────────────────────

  {
    id: 'dash-conversations-badges',
    name: 'Conversations widget: shows project badges for cross-project conversations',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          // The widget only shows conversations that have a latest_note and unread_count > 0
          const unreadWithProject = MOCK_DB.conversations.filter(
            c => c.latest_note && c.unread_count > 0 && c.project?.name
          );
          if (unreadWithProject.length === 0) return; // nothing to assert

          const badges = container.querySelectorAll('[data-testid="conversation-project-badge"]');
          assert(
            badges.length >= 1,
            `Expected at least 1 conversation project badge, found ${badges.length}`
          );
        }
      );
    },
  },

  {
    id: 'dash-conversations-multi-project',
    name: 'Conversations widget: conversations span multiple distinct projects',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const unreadConvs = MOCK_DB.conversations.filter(c => c.latest_note && c.unread_count > 0 && c.project?.name);
          const projectNames = [...new Set(unreadConvs.map(c => c.project.name))];
          if (projectNames.length < 2) return; // skip if only one project represented

          projectNames.forEach(name => {
            assert(
              container.textContent.includes(name),
              `Conversation widget should show project name "${name}"`
            );
          });
        }
      );
    },
  },

  // ─── Tasks widget ───────────────────────────────────────────────────────────

  {
    id: 'dash-tasks-badges',
    name: 'Tasks widget: shows project badges for cross-project tasks',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const tasksWithProject = MOCK_DB.tasks.filter(t => t.project?.name);
          if (tasksWithProject.length === 0) return;

          const badges = container.querySelectorAll('[data-testid="task-project-badge"]');
          assert(
            badges.length >= 1,
            `Expected at least 1 task project badge, found ${badges.length}`
          );
        }
      );
    },
  },

  {
    id: 'dash-tasks-multi-project',
    name: 'Tasks widget: tasks from multiple projects all appear',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const projectNames = [...new Set(MOCK_DB.tasks.filter(t => t.project?.name).map(t => t.project.name))];
          if (projectNames.length < 2) return;

          projectNames.forEach(name => {
            assert(
              container.textContent.includes(name),
              `Task widget should contain project name "${name}"`
            );
          });
        }
      );
    },
  },

  // ─── Navigation buttons ─────────────────────────────────────────────────────

  {
    id: 'dash-nav-buttons',
    name: 'Dashboard: All Projects and Open Calendar navigation buttons exist',
    run: async () => {
      const Dashboard = require('../../Pages/Private/Dashboard').default;
      return await renderWithMock(
        withWrapper(<Dashboard />, '/dashboard'),
        async ({ container }) => {
          const allProjectsBtn = container.querySelector('[data-testid="dashboard-all-projects-btn"]');
          const calendarBtn = container.querySelector('[data-testid="dashboard-open-calendar-btn"]');
          assert(allProjectsBtn !== null, 'All Projects button must exist in statistics section');
          assert(calendarBtn !== null, 'Open Calendar button must exist in calendar section');
        }
      );
    },
  },
];
